import React, { useState, useEffect, useRef } from "react";
import css from "./main.module.css";
import { IoSearchOutline } from "react-icons/io5";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

const AllTems = ({
  categories,
  onCategoryClick,
  onLevelFilter,
  selectedLevel,
  setSelectedLevel,
  allLevels,
  onClearAllFilters,
}) => {
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [selectedLevelGroup, setSelectedLevelGroup] = useState("all");

  const [isVisible, setIsVisible] = useState(true);
  const sectionRef = useRef(null);
  const { t } = useTranslation();

  // Level groups matching code1.html design
  const levelGroups = {
    beginner: {
      label: t("levelBeginnerGroup") || "Beginner",
      levels: [1, 2], // A1 → 1, A2 → 2
      code: "(A1-A2)"
    },
    intermediate: {
      label: t("levelIntermediateGroup") || "Intermediate",
      levels: [3, 4], // B1 → 3, B2 → 4
      code: "(B1-B2)"
    },
    advanced: {
      label: t("levelAdvancedGroup") || "Advanced",
      levels: [5, 6], // C1 → 5, C2 → 6
      code: "(C1-C2)"
    },

  };

  const parentCategories = categories.filter(
    (category) => category.parent === null
  );

  // Create a mapping of category types to find them reliably
  // This approach uses consistent identifiers instead of translated names
  const getCategoryByType = (type) => {
    // You can either use category IDs (if you know them) or create a more robust matching system
    // For now, I'll create a mapping based on common keywords that might exist in the original/base name

    const categoryMappings = {
      travel: ['travel', 'подорож', 'podróż', 'tourism', 'туризм', 'airports', "аеропорти", "lotniska"],
      work: ['work', 'робота', 'praca', 'job', 'career', 'кар\'єра', 'business', 'бізнес'],
      education: ['education', 'освіта', 'edukacja', 'school', 'школа', 'learning', 'навчання'],
      health: ['health', 'здоров\'я', 'zdrowie', 'medical', 'медичн', 'medicine', 'лікування'],
      tech: ['tech', 'technology', 'технолог', 'news', 'новини', 'wiadomości', 'computer', 'комп\'ютер', "Програми", "Aplikacje", "Media społecznościowe"],
      leisure: ['leisure', 'дозвілля', 'rozrywka', 'entertainment', 'розваги', 'hobby', 'хобі', 'sport', 'спорт', "Gotowanie", "Кулінарія", "Book", "Książka", "Книга"],
      daily: ['daily', 'щоденн', 'codzienn', 'life', 'життя', 'życie', 'routine', 'побут']
    };

    const keywords = categoryMappings[type] || [];

    return parentCategories.find(cat => {
      const catName = cat.name.toLowerCase();
      return keywords.some(keyword => catName.includes(keyword.toLowerCase()));
    });
  };

  // Alternative approach: If you have access to category slugs or original English names
  // You could also add a `slug` or `type` field to your categories in the backend
  // const getCategoryBySlug = (slug) => {
  //   return parentCategories.find(cat => cat.slug === slug);
  // };

  useEffect(() => {
    const storedPurpose = localStorage.getItem("selectedPurpose");
    if (storedPurpose) {
      try {
        const parsed = JSON.parse(storedPurpose);
        if (parsed.categories && Array.isArray(parsed.categories)) {
          setSelectedCategoryIds(parsed.categories);
        }
      } catch (e) {
        console.warn("⚠ Failed to parse stored purpose", e);
      }
    }
  }, []);

  // Sync selectedLevelGroup with selectedLevel prop from parent
  useEffect(() => {
    if (!selectedLevel) {
      setSelectedLevelGroup("all");
      return;
    }

    // Check if selectedLevel is a comma-separated string (group selection)
    if (typeof selectedLevel === 'string' && selectedLevel.includes(',')) {
      const levels = selectedLevel.split(',').map(Number);
      // Find which group contains these levels
      const foundGroup = Object.entries(levelGroups).find(([key, group]) => {
        return group.levels.every(level => levels.includes(level)) &&
          levels.every(level => group.levels.includes(level));
      });

      if (foundGroup) {
        setSelectedLevelGroup(foundGroup[0]);
      } else {
        setSelectedLevelGroup("all");
      }
    } else {
      // Single level selection
      const levelNum = typeof selectedLevel === 'number' ? selectedLevel : parseInt(selectedLevel);
      const foundGroup = Object.entries(levelGroups).find(([key, group]) => {
        return group.levels.includes(levelNum);
      });

      if (foundGroup) {
        setSelectedLevelGroup(foundGroup[0]);
      } else {
        setSelectedLevelGroup("all");
      }
    }
  }, [selectedLevel, levelGroups]);

  // Intersection Observer for reveal animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Reset visibility when language changes
  useEffect(() => {
    // Force re-render of the section title when language changes
    setIsVisible(false);
    setTimeout(() => {
      setIsVisible(true);
    }, 50);
  }, [i18n.language]);

  const handleLevelSelect = (levelKey) => {
    setSelectedLevelGroup(levelKey);

    if (levelKey === "all") {
      localStorage.removeItem("selectedLevel");
      setSelectedLevel(null); // parent
      if (typeof onLevelFilter === "function") {
        onLevelFilter(null);
      }
      return;
    }

    if (levelGroups[levelKey]) {
      const groupLevels = levelGroups[levelKey].levels;
      const levelString = groupLevels.join(",");
      localStorage.setItem("selectedLevel", levelString);
      setSelectedLevel(levelString); // Pass the full group as string to parent
      if (typeof onLevelFilter === "function") {
        onLevelFilter(levelString);
      }
    }
  }

  const toggleCategory = (categoryId) => {
    if (categoryId === 'all') {
      // Clear all selections and select only 'all'
      setSelectedCategoryIds([]);
      if (typeof onClearAllFilters === "function") {
        onClearAllFilters();
      }
      return;
    }

    // Handle case where category might not be found
    if (!categoryId) {
      console.warn("Category not found for this language");
      return;
    }

    let updated;
    if (selectedCategoryIds.includes(categoryId)) {
      updated = selectedCategoryIds.filter((id) => id !== categoryId);
    } else {
      updated = [...selectedCategoryIds, categoryId];
    }
    setSelectedCategoryIds(updated);
    onCategoryClick(categoryId);
  };

  return (
    <div className={css.container}>
      {/* Section Header */}
      <div className={css.sectionHeader}>
        <h2 className={`${css.sectionTitle} ${isVisible ? css.reveal : ""}`}>
          {(() => {
            const sectionTitles = {
              en: ["Find", "videos", "for", "your", "level"],
              uk: ["Знайди", "відео", "для", "свого", "рівня"],
              pl: ["Znajdź", "filmy", "na", "swoim", "poziomie"]
            };

            const currentLang = i18n.language || 'en';
            const words = sectionTitles[currentLang] || sectionTitles.en;

            return words.map((word, index) => (
              <span key={`${currentLang}-${index}`} className={css.word}>
                {word}
              </span>
            ));
          })()}
        </h2>
        <p className={`${css.sectionSubtitle} ${isVisible ? css.reveal : ""}`}>
          {t("Choose your English level and topics that interest you")}
        </p>
      </div>

      {/* Level Selector */}
      <div className={css.levelSelector}>
        <div className={css.levelChips}>
          <button
            className={`${css.levelChip} ${selectedLevelGroup === "all" ? css.active : ""}`}
            onClick={() => handleLevelSelect("all")}
          >
            <span className={css.levelLabel}>{t("All Levels")}</span>
          </button>
          {Object.entries(levelGroups).map(([key, group]) => (
            <button
              key={key}
              className={`${css.levelChip} ${selectedLevelGroup === key ? css.active : ""}`}
              onClick={() => {
                handleLevelSelect(key);
                console.log(key);
              }}
            >
              <span className={css.levelLabel}>{group.label}</span>
              <span className={css.levelCode}>{group.code}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Categories Section */}
      <div className={css.categories}>
        {/* All Categories Item */}
        <div
          className={`${css.categoryItem} ${selectedCategoryIds.length === 0 ? css.active : ""}`}
          onClick={() => toggleCategory('all')}
          data-category="all"
        >
          <div className={`${css.categoryIcon} ${css.all}`}>
            <span>🌐</span>
          </div>
          <span className={css.categoryName}>{t("All Categories")}</span>
        </div>

        {/* Category Items - Using the new getCategoryByType function */}
        {(() => {
          const travelCategory = getCategoryByType('travel');
          return travelCategory ? (
            <div
              className={`${css.categoryItem} ${selectedCategoryIds.includes(travelCategory.id) ? css.active : ""}`}
              onClick={() => toggleCategory(travelCategory.id)}
              data-category="travel"
            >
              <div className={`${css.categoryIcon} ${css.travel}`}>
                <span>✈️</span>
              </div>
              <span className={css.categoryName}>{t("Travel")}</span>
            </div>
          ) : null;
        })()}

        {(() => {
          const workCategory = getCategoryByType('work');
          return workCategory ? (
            <div
              className={`${css.categoryItem} ${selectedCategoryIds.includes(workCategory.id) ? css.active : ""}`}
              onClick={() => toggleCategory(workCategory.id)}
              data-category="work"
            >
              <div className={`${css.categoryIcon} ${css.work}`}>
                <span>💼</span>
              </div>
              <span className={css.categoryName}>{t("Work")}</span>
            </div>
          ) : null;
        })()}

        {(() => {
          const educationCategory = getCategoryByType('education');
          return educationCategory ? (
            <div
              className={`${css.categoryItem} ${selectedCategoryIds.includes(educationCategory.id) ? css.active : ""}`}
              onClick={() => toggleCategory(educationCategory.id)}
              data-category="education"
            >
              <div className={`${css.categoryIcon} ${css.education}`}>
                <span>📚</span>
              </div>
              <span className={css.categoryName}>{t("Education")}</span>
            </div>
          ) : null;
        })()}

        {(() => {
          const healthCategory = getCategoryByType('health');
          return healthCategory ? (
            <div
              className={`${css.categoryItem} ${selectedCategoryIds.includes(healthCategory.id) ? css.active : ""}`}
              onClick={() => toggleCategory(healthCategory.id)}
              data-category="health"
            >
              <div className={`${css.categoryIcon} ${css.health}`}>
                <span>❤️</span>
              </div>
              <span className={css.categoryName}>{t("Health")}</span>
            </div>
          ) : null;
        })()}

        {(() => {
          const techCategory = getCategoryByType('tech');
          return techCategory ? (
            <div
              className={`${css.categoryItem} ${selectedCategoryIds.includes(techCategory.id) ? css.active : ""}`}
              onClick={() => toggleCategory(techCategory.id)}
              data-category="tech"
            >
              <div className={`${css.categoryIcon} ${css.tech}`}>
                <span>📱</span>
              </div>
              <span className={css.categoryName}>{t("News & Technology")}</span>
            </div>
          ) : null;
        })()}

        {(() => {
          const leisureCategory = getCategoryByType('leisure');
          return leisureCategory ? (
            <div
              className={`${css.categoryItem} ${selectedCategoryIds.includes(leisureCategory.id) ? css.active : ""}`}
              onClick={() => toggleCategory(leisureCategory.id)}
              data-category="leisure"
            >
              <div className={`${css.categoryIcon} ${css.leisure}`}>
                <span>🎮</span>
              </div>
              <span className={css.categoryName}>{t("Leisure")}</span>
            </div>
          ) : null;
        })()}

        {(() => {
          const dailyCategory = getCategoryByType('daily');
          return dailyCategory ? (
            <div
              className={`${css.categoryItem} ${selectedCategoryIds.includes(dailyCategory.id) ? css.active : ""}`}
              onClick={() => toggleCategory(dailyCategory.id)}
              data-category="daily"
            >
              <div className={`${css.categoryIcon} ${css.daily}`}>
                <span>🏠</span>
              </div>
              <span className={css.categoryName}>{t("Daily Life")}</span>
            </div>
          ) : null;
        })()}
      </div>
    </div>
  );
};

export default AllTems;